/* @flow */
import gql from 'graphql-tag';
import {isToday, parseISO} from 'date-fns';
import {evaluationIsVariant} from '@homeaway/react-experiment';
import {fragments as destinationBreadcrumbsFragments} from '../components/Breadcrumb/Breadcrumb';
import {destinationCarouselFragments} from '../components/DestinationCarousel/DestinationCarousel.fragments';
import {fragments as hitCollectionFragments} from '../components/HitCollection/HitCollection';
import {mapFragments} from '../components/Map/Map.fragments';
import {fragments as pagerFragments} from '../components/Pager/Pager';
import {fragments as destinationMessage} from '../components/SearchBannerContainer/destinationMessage';
import {fragments as filterCountsFragments} from './filteredCountSearchRequestQuery';
import {fragments as expandedGroupsFragments} from '../components/ExpandedGroups/ExpandedGroups';
import {fragments as analyticsDataLayerFragments} from '../components/AnalyticsDataLayer/AnalyticsDataLayer';
import {fragments as searchTermCarouselFragments} from '../components/SearchTermCarousel/SearchTermCarousel';
import {fragments as internalToolsFragments} from '../components/InternalTools/InternalTools';
import {fragments as seoMetaDataParamsFragments} from '../components/withSeoMetaData/withSeoMetaData';
import {fragments as globalInlineFragments} from '../components/SearchBannerContainer/GlobalInlineMessage';
import {fragments as globalBannerContainerFragments} from '../components/GlobalBannerContainer/GlobalBannerContainer';
import {flexibleDatesFragments} from '../components/FlexibleDates/FlexibleDates.fragments';
import transformReduxParamsToSearch from './transformReduxParamsToSearchRequest';
import {
    VRBO_WEB_SERP_FEWER_INITIAL_RESULTS,
    VRBO_WEB_GLOBAL_MESSAGING_BANNER,
    VRBO_WEB_GLOBAL_MESSAGING_ALERT,
    VRBO_RECO_LARGE_SEARCH_DESTINO
} from '../../universal/abacus/experiments';

import type {State as ReduxState} from '../reducers/index';
import type {
    SearchParamsType,
    SearchRequestType
} from '../reducers/searchParams';
import type DestinationMessage from '@homeaway/graphql-traveler-urgency-messaging';
import type DiscoveryFeeds from '@homeaway/graphql-traveler-discovery';
import type {Shape as ExpandedGroupsShape} from '../components/ExpandedGroups/ExpandedGroups';
import type {FilterGroupType} from '@homeaway/react-search-filters';
import type {InternalToolsType} from '../components/InternalTools/InternalTools';
import type {ListingShape} from '../components/Hit/Hit';
import type {MapViewport} from '../components/Map/Map';
import type {PinnedListing} from '../components/HitCollection/HitCollection';
import type {SearchReviewSummary} from '../components/SearchReviews/SearchReviews';

type AbacusState = $PropertyType<ReduxState, 'abacus'>;
type SystemState = $PropertyType<ReduxState, 'system'>;

export type Coordinates = {
    latitude: number,
    longitude: number
};

// typed based on shape of `querySelectionSet` fragment below
export type Geography = {
    name?: string,
    description?: string,
    location?: ?Coordinates,
    isGeocoded?: boolean,
    shouldShowMapCentralPin: boolean,
    lbsId?: string,
    primaryGeoType?: string,
    breadcrumbs: Geography[],
    countryCode?: string,
    gaiaId?: string
};

export type BreadcrumbShape = {
    url: string,
    name: string
};

export type DestinationBreadcrumbShape = {
    name: string,
    url: string
};

export type DestinationShape = {
    breadcrumbs?: BreadcrumbShape[],
    links?: BreadcrumbShape[]
};

export type PlaceShape = {
    place?: {
        name: {
            full: string,
            simple: string
        }
    }
};

export type RecommendedDestinations = {
    searchTermUuid: string,
    imageHref?: string,
    breadcrumbs?: PlaceShape[]
};

export type DestinationRecomendationsResponse = {
    clientRequestId: string,
    recommendedDestinations?: RecommendedDestinations[]
};

export type CoreFilters = {
    adults?: number,
    children?: number,
    pets?: number,
    minBedrooms?: number,
    maxBedrooms?: number,
    minSleeps?: number,
    maxSleeps?: number,
    minNightlyPrice?: number,
    maxNightlyPrice?: number,
    minTotalPrice?: number,
    maxTotalPrice?: number,
    minBathrooms?: number,
    maxBathrooms?: number
};

export type SearchDateRange = {
    arrivalDate: string,
    departureDate: string
};

// typed based on shape of `querySelectionSet` fragment below
export type TypeaheadSuggestion = {
    uuid: string,
    term: string,
    name: string
};

export type ParsedSearchParams = {
    q?: string,
    coreFilters?: CoreFilters,
    dates?: SearchDateRange,
    sort: string
};

export type GlobalMessages = {
    alert?: mixed,
    banner?: mixed
};

export type PercentBookedResult = {
    currentPercentBooked?: number
};

/**
 * This type represents the result of the main search query. If you update the
 * query below, you must keep this type in sync to surface potential errors
 * throughout the rest of the codebase that unit tests may not catch.
 */
export type SearchRequestQueryResults = {
    destination?: DestinationShape,
    destinationBreadcrumbs?: DestinationBreadcrumbShape[],
    destinationRecommendationResponse?: DestinationRecomendationsResponse,
    destinationMessage?: DestinationMessage,
    discoveryXploreFeeds?: DiscoveryFeeds,
    expandedGroups?: ExpandedGroupsShape,
    filterGroups?: FilterGroupType[],
    geography?: ?Geography,
    globalMessages?: GlobalMessages,
    internalTools?: InternalToolsType,
    listings?: ListingShape[],
    liveRegionRedirectUrl?: string,
    mapViewport: MapViewport,
    page?: number,
    pageCount?: number,
    pageSize?: number,
    parsedParams?: ParsedSearchParams,
    percentBooked?: PercentBookedResult,
    propertyRedirectUrl?: string,
    pinnedListing?: PinnedListing,
    queryUUID?: string,
    resultCount?: number,
    topRecentReviews?: SearchReviewSummary[],
    typeaheadSuggestion?: ?TypeaheadSuggestion
};

export type SearchRequestQueryResponseData = {
    results: ?SearchRequestQueryResults,
    requestmarker: string,
    error?: Error,
    loading: boolean,
    variables: {
        request?: SearchRequestType, // SearchRequestQuery params
        url?: string
    }
};

export const searchRequestQueryVariablesOverrides = (
    params: SearchParamsType,
    abacus: AbacusState,
    system: SystemState,
    isFilteredSearchRequestCountQuery: boolean = false
): SearchParamsType => {
    const {badBotPageSize, isBadBot} = system;
    let newParams: SearchParamsType = {...params};
    const {arrival} = newParams;

    // Handle variable page sizing
    if (!isFilteredSearchRequestCountQuery) {
        if (isBadBot) {
            newParams.pageSize = badBotPageSize;
        }

        if (
            !isBadBot &&
            evaluationIsVariant(abacus[VRBO_WEB_SERP_FEWER_INITIAL_RESULTS], 1)
        ) {
            newParams.pageSize = 25;
        }
    }

    // If user is filtering by total price and has dates,
    // use min/max TotalPrice params (clear out nightly price)
    // otherwise, use min/maxNightlyPriceParams (clear out total price)
    if (params.filterByTotalPrice && params.arrival && params.departure) {
        newParams.minNightlyPrice = 0;
        newParams.maxNightlyPrice = null;
    } else if (params.minTotalPrice || params.maxTotalPrice) {
        newParams.minTotalPrice = null;
        newParams.maxTotalPrice = null;
    }

    // Why is this business logic buried here? We need to find a better place for it.
    if (arrival && isToday(parseISO(arrival))) {
        newParams.isSameDaySelected = true;
    }

    return newParams;
};

export const searchRequestQueryVariables = (
    params: SearchParamsType,
    abacus: AbacusState,
    system: SystemState,
    // TODO: we need to use the same variables for both searching and fetching
    // filter counts, but this is a clumsy way to tell the override function not
    // to mess with filter count pageSize, which needs to be set low so that
    // filter counts can be fetched cheaply.
    isFilteredSearchRequestCountQuery: boolean = false
) => {
    const newParams = searchRequestQueryVariablesOverrides(
        params,
        abacus,
        system,
        isFilteredSearchRequestCountQuery
    );

    // Create a boolean variable for each experiment that has an associated
    // GraphQL fragment that needs conditionally included in the query.
    const experiments = {
        vrbo_web_global_messaging_alert: evaluationIsVariant(
            abacus[VRBO_WEB_GLOBAL_MESSAGING_ALERT],
            'on'
        ),
        vrbo_web_global_messaging_banner: evaluationIsVariant(
            abacus[VRBO_WEB_GLOBAL_MESSAGING_BANNER],
            'on'
        ),
        Vrbo_reco_large_search_destino: evaluationIsVariant(
            abacus[VRBO_RECO_LARGE_SEARCH_DESTINO],
            'on'
        )
    };

    return {
        filterCounts: system.searchFilterCounts,
        request: transformReduxParamsToSearch(newParams),
        ...experiments
    };
};

// TODO: why does this fragment exist? This is a leftover from making GTS core
// and I presume was previously shared by SRS and GTS queries. These fields need
// to be extracted to fragments colocated with the components or modules that
// use them
const querySelectionSet = gql`
    fragment querySelectionSet on SearchResult {
        id
        typeaheadSuggestion {
            uuid
            term
            name
        }
        geography {
            lbsId
            gaiaId
            location {
                latitude
                longitude
            }
            isGeocoded
            shouldShowMapCentralPin
        }
        propertyRedirectUrl
    }
`;

export const SEARCH_REQUEST_QUERY_NAME = 'SearchRequestQuery';

export const FORCE_GRAPHQL_ERROR_HEADER = 'ha-serp-force-graphql-err';

const searchRequestQuery = gql`
    query ${SEARCH_REQUEST_QUERY_NAME}(
        $request: SearchResultRequest!
        $filterCounts: Boolean!
        $vrbo_web_global_messaging_alert: Boolean!
        $vrbo_web_global_messaging_banner: Boolean!
        $Vrbo_reco_large_search_destino: Boolean!
    ) {
        results: search(request: $request) {
            ...querySelectionSet
            ...DestinationBreadcrumbsSearchResult
            ...DestinationCarouselSearchResult @include(if: $Vrbo_reco_large_search_destino)
            ...DestinationMessageSearchResult
            ...FilterCountsSearchRequestResult
            ...HitCollectionSearchResult
            ...ADLSearchResult
            ...MapSearchResult
            ...ExpandedGroupsSearchResult
            ...PagerSearchResult
            ...SearchTermCarouselSearchResult
            ...InternalToolsSearchResult
            ...SEOMetaDataParamsSearchResult
            ...GlobalInlineMessageSearchResult @include(if: $vrbo_web_global_messaging_alert)
            ...GlobalBannerContainerSearchResult @include(if: $vrbo_web_global_messaging_banner)
            ...FlexibleDatesSearchResult
        }
        ...RequestMarkerFragment
    }
    ${querySelectionSet}
    ${destinationBreadcrumbsFragments.SearchResult}
    ${destinationCarouselFragments.SearchResult}
    ${hitCollectionFragments.SearchResult}
    ${expandedGroupsFragments.SearchResult}
    ${filterCountsFragments.SearchResult}
    ${mapFragments.SearchResult}
    ${pagerFragments.SearchResult}
    ${destinationMessage.SearchResult}
    ${analyticsDataLayerFragments.SearchResult}
    ${analyticsDataLayerFragments.Query}
    ${searchTermCarouselFragments.SearchResult}
    ${internalToolsFragments.SearchResult}
    ${seoMetaDataParamsFragments.SearchResult}
    ${globalInlineFragments.SearchResult}
    ${globalBannerContainerFragments.SearchResult}
    ${flexibleDatesFragments.SearchResult}
`;

export default searchRequestQuery;
